"use client";
/* eslint-disable no-param-reassign */
import type { StoreData } from "@features/stores/types";
import { shoeConventionForSite } from "@config/site/site-config";

import { useIsomorphicLayoutEffect } from "@ecco/ui/use-isomorphic-layout-effect";
import { produce } from "immer";
import { merge } from "lodash-es";
import { createContext, use, useEffect, useRef } from "react";
import { createStore, useStore as useZustandStore } from "zustand";
import { persist } from "zustand/middleware";
import { updateProfile } from "./helpers/profile";
import { initialState } from "./initialState";
import type { Customer } from "@lib/api-services/services/profile-service/types";
import type { StoreDataType, StoreState, Tracking, InitialFiltersType } from "./types";
import type { ProductVariant } from "@lib/product/types";

const PERSIST_KEY = "ecco-store";

export const initializeStore = (preloadedState = {}) => {
    return createStore<StoreState>()(
        persist(
            (set, get) => {
                // Wrapper around Immer "set" to for added type safety
                const setState = (fn: (state: StoreState) => void) => set(produce<StoreState>(fn));

                return {
                    data: merge({}, initialState, preloadedState),
                    setLocked: (payload) =>
                        setState((state) => {
                            state.data.isLocked = payload;
                        }),
                    setActiveData: (cartAndWishlist) =>
                        setState((state) => {
                            state.data.me.cartInfo.cart = cartAndWishlist.cart;
                            state.data.me.wishlistInfo.wishlist = cartAndWishlist.wishlist;
                        }),
                    clearMe: () =>
                        setState((state) => {
                            state.data.me = initialState.me;
                        }),
                    setCart: (cart) =>
                        setState((state) => {
                            state.data.me.cartInfo.cart = cart;
                        }),
                    clearCart: () =>
                        setState((state) => {
                            state.data.me.cartInfo.cart = undefined;
                            state.data.me.cartInfo.shopperConversionId = undefined;
                        }),
                    setWishlist: (wishlist) =>
                        setState((state) => {
                            state.data.me.wishlistInfo.wishlist = wishlist;
                        }),
                    getWishlist: () => get().data.me.wishlistInfo.wishlist,
                    setGlobalShoeSizeConvention: (globalShoeSizeConvention) =>
                        setState((state) => {
                            state.data.me.globalShoeSizeConvention = globalShoeSizeConvention;
                        }),
                    clearWishlist: () =>
                        setState((state) => {
                            state.data.me.wishlistInfo.wishlist = undefined;
                            state.data.me.wishlistInfo.masterProducts = undefined;
                        }),
                    setWishlistIsExecuted: (isExecuted) =>
                        setState((state) => {
                            state.data.me.wishlistInfo.isExecuted = isExecuted;
                        }),
                    setAddToCartIsExecuted: (isExecuted) =>
                        setState((state) => {
                            state.data.me.cartInfo.isExecuted = isExecuted;
                        }),
                    setMasterProducts: (masterProducts) =>
                        setState((state) => {
                            state.data.me.cartInfo.masterProducts = masterProducts;
                        }),
                    setWishlistMasterProducts: (masterProducts) =>
                        setState((state) => {
                            state.data.me.wishlistInfo.masterProducts = masterProducts;
                        }),
                    setResultsCount: (payload) =>
                        setState((state) => {
                            state.data.products.resultsCount = payload;
                        }),
                    setLastLoadedPage: (payload) =>
                        setState((state) => {
                            state.data.products.lastLoadedPage = payload;
                        }),
                    setLastLoadedPlpContainerHeight: (payload) =>
                        setState((state) => {
                            state.data.products.lastVisitedPlpContainerHeight = payload;
                        }),
                    setInitialFilters: (payload: InitialFiltersType) =>
                        setState((state) => {
                            state.data.products.initialFilters = payload;
                        }),
                    setLastVisitedPlp: (payload: string | null) =>
                        setState((state) => {
                            state.data.products.lastVisitedPlp = payload;
                        }),
                    setDraftPromoCode: (draftPromoCode: string) =>
                        setState((state) => {
                            state.data.draftPromoCode = draftPromoCode;
                        }),
                    setHasFreshData: (hasFreshData: boolean) =>
                        setState((state) => {
                            state.data.hasFreshData = hasFreshData;
                        }),
                    setCustomer: (customer: Customer) =>
                        setState((state) => {
                            state.data.me.customer = customer;
                        }),
                    setMyAddresses: (customer: Customer) =>
                        setState((state) => {
                            if (!state.data.me.customer) {
                                state.data.me.customer = customer;
                                return;
                            }

                            state.data.me.customer.addresses = customer.addresses;
                        }),
                    getStoreData: () => get().data.storeData,
                    setStoreData: (newData: StoreData) =>
                        setState((state) => {
                            state.data.storeData = newData;
                        }),
                    setStores: (stores: StoreData[]) =>
                        setState((state) => {
                            state.data.stores = stores;
                        }),
                    getStores: () => get().data.stores,
                    getPolicyAccepted: () => get().data.policyAccepted,
                    setPolicyAccepted: (policyAccepted: boolean) =>
                        setState((state) => {
                            state.data.policyAccepted = policyAccepted;
                        }),
                    getParcelShopData: () => get().data.parcelShopData,
                    setParcelShopData: (newData: StoreData) =>
                        setState((state) => {
                            state.data.parcelShopData = newData;
                        }),
                    setMyProfile: (customer: Customer) => setState(updateProfile(customer)),
                    setUtmParams: (utmParams: Tracking["utm"]) =>
                        setState((state) => {
                            state.data.tracking.utm = utmParams;
                        }),
                    getUtmParams: () => get().data.tracking.utm,
                    getOrCreateShopperConversionId: () => {
                        const existing = get().data.me.cartInfo.shopperConversionId;
                        if (existing) return existing;
                        const id = crypto.randomUUID();
                        setState((state) => {
                            state.data.me.cartInfo.shopperConversionId = id;
                        });
                        return id;
                    },
                    getCart: () => get().data.me.cartInfo.cart,
                    getMasterProducts: () => get().data.me.cartInfo.masterProducts,
                    setActiveProduct: (
                        activeProduct: {
                            colorCode: string;
                            variant: ProductVariant | null;
                            isAvailable: boolean;
                        } | null
                    ) =>
                        setState((state) => {
                            state.data.pdpState.activeProduct = activeProduct;
                        }),
                    setIsOpenAddedToBagModal: (modalState: boolean) =>
                        setState((state) => {
                            state.data.pdpState.isOpenAddedToBagModal = modalState;
                        }),
                    setTempCustomerData: ({ firstName, lastName, email }) =>
                        setState((state) => {
                            state.data.tempCustomerData.firstName = firstName;
                            state.data.tempCustomerData.lastName = lastName;
                            state.data.tempCustomerData.email = email;
                        }),
                    getTempCustomerData: () => get().data.tempCustomerData,
                    clearTempCustomerData: () =>
                        setState((state) => {
                            state.data.tempCustomerData.firstName = null;
                            state.data.tempCustomerData.lastName = null;
                            state.data.tempCustomerData.email = null;
                        }),
                    setClickAndCollectStores: (stores: StoreData[]) =>
                        setState((state) => {
                            state.data.pdpState.clickAndCollectStores = stores;
                        }),
                    setPreviewMode: (isPreview: boolean) =>
                        set(
                            produce((state) => {
                                state.data.isPreviewMode = isPreview;
                            })
                        ),
                    getPreviewMode: () => get().data.isPreviewMode,
                    setGalleryOpen: (isOpen) =>
                        setState((state) => {
                            state.data.productImageGallery.isGalleryOpen = isOpen;
                        }),
                    setGalleryImageIndex: (index) =>
                        setState((state) => {
                            state.data.productImageGallery.galleryImageIndex = index;
                        }),
                    setHasActiveTimer: (hasActiveTimer) =>
                        setState((state) => {
                            state.data.hasActiveTimer = hasActiveTimer;
                        }),
                };
            },
            {
                partialize: (state) => ({
                    data: {
                        me: {
                            globalShoeSizeConvention: state.data.me.globalShoeSizeConvention,
                            customer: state.data.me.customer,
                            // Adyen requires shopperConversionId to remain stable for the
                            // entire checkout session — including across full-page reloads
                            // and 3DS redirects. clearCart() resets it after order success.
                            cartInfo: {
                                shopperConversionId: state.data.me.cartInfo.shopperConversionId,
                            },
                        },
                        storeData: state.data.storeData,
                        parcelShopData: state.data.parcelShopData,
                        tempCustomerData: state.data.tempCustomerData,
                    },
                }),
                name: PERSIST_KEY,
                merge: (persistedState: unknown, currentState: StoreState): StoreState => {
                    if (persistedState === null || typeof persistedState !== "object") {
                        return currentState;
                    }

                    const safePersisted = persistedState as Partial<StoreState>;

                    const persistedConvention = safePersisted.data?.me?.globalShoeSizeConvention;
                    const isValidConvention =
                        persistedConvention && shoeConventionForSite.includes(persistedConvention);

                    if (!isValidConvention) {
                        return merge(currentState, {
                            data: {
                                me: {
                                    globalShoeSizeConvention: shoeConventionForSite[0],
                                },
                            },
                        });
                    }

                    return merge(currentState, safePersisted);
                },
            }
        )
    );
};

const ZustandContext = createContext<ReturnType<typeof initializeStore>>(
    initializeStore(initialState)
);

export const ZustandProvider = ({
    children,
    initialState = {},
}: {
    children: React.ReactNode;
    initialState?: Partial<StoreDataType>;
}) => {
    const storeRef = useRef<ReturnType<typeof initializeStore> | undefined>(undefined);
    if (!storeRef.current) {
        storeRef.current = initializeStore(initialState);
    }

    useEffect(() => {
        const rehydrate = () => {
            storeRef.current?.persist.rehydrate();
        };

        const onStorage = (e: StorageEvent) => {
            if (e.key === PERSIST_KEY) rehydrate();
        };

        const onVisibility = () => {
            if (document.visibilityState === "visible") rehydrate();
        };

        // Sync storage events across tabs
        window.addEventListener("storage", onStorage);
        // Sync when changing tabs
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("storage", onStorage);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    return <ZustandContext value={storeRef.current}>{children}</ZustandContext>;
};

export function useStore<T>(selector: (state: StoreState) => T) {
    const store = use(ZustandContext);

    if (!store) throw new Error("Store is missing the provider");

    return useZustandStore(store, selector) as T;
}

export const useStoreApi = () => {
    return use(ZustandContext);
};

export function useCreateStore(initialState: { data: StoreDataType }) {
    // For CSR, always re-use same store.
    const store = initializeStore(initialState);
    // And if initialState changes, then merge states in the next render cycle.
    //
    // eslint complaining "React Hooks must be called in the exact same order in every component render"
    // is ignorable as this code runs in same order in a given environment
    useIsomorphicLayoutEffect(() => {
        if (initialState && store) {
            store.setState({
                ...store.getState(),
                ...initialState,
            });
        }
    }, [initialState]);

    return () => store;
}
